import { forwardRef, useMemo } from 'react';

import {
  useExplorationActions,
  useExplorationState,
  useFilteredVolumes,
} from '../context/ExplorationContext';
import { currentDrillTailType } from '../lib/bubble/ancestryChips';
import {
  breadcrumbStatusTagLabel,
  compactDrillPathForBreadcrumb,
  formatBreadcrumbLabel,
  getBreadcrumbTailDrillIndex,
  segmentsToDisplayItems,
} from '../lib/breadcrumb/buildBreadcrumbDisplay';
import { entityTypeToTagVariant, getEntityChipStyle } from '../lib/entityIcons';
import { displayPathStrings, emptyDrillLocation, locationAtDisplayIndex } from '../lib/drill/drillLocation';
import { volumeDisplayName } from '../lib/formatBytes';
import { volumesInCurrentScope } from '../lib/sidebar/volumesInCurrentScope';
import Button from '~/shared/components/ButtonsV2';
import Tag from '~/shared/components/Tag';
import Typography from '~/shared/components/Typography';

function BreadcrumbSeparator() {
  return (
    <Typography variant="caption" color="text-gray-600" aria-hidden="true" className="shrink-0">
      ›
    </Typography>
  );
}

function BreadcrumbLabel({
  name,
  color,
  fontWeight,
}: {
  name: string;
  color: string;
  fontWeight?: 500;
}) {
  return (
    <Typography variant="caption" fontWeight={fontWeight} color={color} title={name}>
      {formatBreadcrumbLabel(name)}
    </Typography>
  );
}

const ExplorationBreadcrumb = forwardRef<HTMLElement>(function ExplorationBreadcrumb(_props, ref) {
  const state = useExplorationState();
  const actions = useExplorationActions();
  const volumes = useFilteredVolumes();

  const location = state.drillPath;

  const effectiveDrillPathKeys = useMemo(
    () => displayPathStrings(location, { groupByAccount: state.groupByAccount }),
    [location, state.groupByAccount]
  );

  const selectedVolume = state.selectedNode?.kind === 'volume' ? state.selectedNode.volume : null;
  const volumeLabel = selectedVolume ? volumeDisplayName(selectedVolume) : null;
  const selectedInstance =
    state.selectedNode?.kind === 'instance' ? state.selectedNode.instanceId : null;

  const segments =
    selectedVolume && volumeLabel
      ? [...effectiveDrillPathKeys, volumeLabel]
      : selectedInstance
        ? effectiveDrillPathKeys.at(-1) === selectedInstance
          ? effectiveDrillPathKeys
          : [...effectiveDrillPathKeys, selectedInstance]
        : effectiveDrillPathKeys;

  const breadcrumbOpts = {
    secondary: state.secondary,
    view: state.view,
  };
  const scopedVolumes = useMemo(
    () =>
      volumesInCurrentScope(volumes, {
        secondary: state.secondary,
        location,
        groupByAccount: state.groupByAccount,
        tagKey: state.tagKey,
        view: state.view,
      }),
    [
      volumes,
      state.secondary,
      location,
      state.groupByAccount,
      state.tagKey,
      state.view,
    ]
  );

  if (!segments.length) return null;

  const drillDisplaySegments = compactDrillPathForBreadcrumb(effectiveDrillPathKeys, breadcrumbOpts);
  const displaySegments =
    segments.length > effectiveDrillPathKeys.length
      ? [
          ...drillDisplaySegments,
          {
            name: segments[segments.length - 1]!,
            drillIndex: effectiveDrillPathKeys.length,
          },
        ]
      : drillDisplaySegments;
  const displayItems = segmentsToDisplayItems(displaySegments);
  const tailDrillIndex = getBreadcrumbTailDrillIndex(displayItems);

  const tailType = selectedVolume
    ? 'volume'
    : selectedInstance
      ? 'instance'
      : currentDrillTailType({ location, secondary: state.secondary });
  const statusTagLabel = breadcrumbStatusTagLabel(effectiveDrillPathKeys, {
    ...breadcrumbOpts,
    volumes: scopedVolumes,
  });
  const tailTagLabel = statusTagLabel ?? getEntityChipStyle(tailType).label;
  const tailTagVariant = entityTypeToTagVariant(statusTagLabel ? 'status' : tailType);

  const navigateToDrillIndex = (index: number) => {
    actions.setDrillPath(locationAtDisplayIndex(location, index, { groupByAccount: state.groupByAccount }));
  };

  return (
    <nav
      ref={ref}
      aria-label="Exploration drill path"
      className="flex items-center gap-2 min-w-0 shrink overflow-hidden bg-white/92 border border-primary-100 rounded-lg px-3 py-1.5 !h-9"
    >
      <Button
        type="button"
        variant="text"
        size="sm"
        className="!h-auto !p-0 !min-w-0 shrink-0"
        onClick={() => actions.setDrillPath(emptyDrillLocation())}
      >
        <Typography variant="caption" color="text-gray-600">
          All
        </Typography>
      </Button>
      <div className="flex items-center gap-2 min-w-0 overflow-hidden">
        {displayItems.map((item, i) => {
          if (item.kind === 'collapse') {
            const hiddenPath = item.hiddenNames.join(' › ');
            return (
              <span key={`collapse-${i}`} className="flex items-center gap-2 shrink-0">
                <BreadcrumbSeparator />
                <Typography
                  variant="caption"
                  color="text-gray-600"
                  className="shrink-0"
                  title={hiddenPath}
                  aria-label={`Collapsed path: ${hiddenPath}`}
                >
                  …
                </Typography>
              </span>
            );
          }

          const isTail = item.drillIndex === tailDrillIndex;

          return (
            <span
              key={`${item.name}-${item.drillIndex}`}
              className="flex items-center gap-2 shrink-0"
            >
              <BreadcrumbSeparator />
              {isTail ? (
                <span
                  className="flex items-center gap-1.5 shrink-0"
                  aria-current="location"
                  aria-label={item.name}
                  title={item.name}
                >
                  <BreadcrumbLabel name={item.name} fontWeight={500} color="text-primary-900" />
                  <Tag
                    variant={tailTagVariant}
                    size="xs"
                    text={tailTagLabel}
                    className="shrink-0"
                  />
                </span>
              ) : (
                <Button
                  type="button"
                  variant="text"
                  size="sm"
                  className="!h-auto !p-0 !min-w-0 shrink-0"
                  aria-label={`Navigate to ${item.name}`}
                  onClick={() => navigateToDrillIndex(item.drillIndex)}
                >
                  <BreadcrumbLabel name={item.name} color="text-gray-600" />
                </Button>
              )}
            </span>
          );
        })}
      </div>
    </nav>
  );
});

export default ExplorationBreadcrumb;
